import { useEffect, useState } from 'react'
import { Badge } from './Badge'

export interface CountdownProps {
  /** Échéance (ISO 8601). L'heure faisant foi reste celle du serveur. */
  deadline: string
  /** Texte une fois l'échéance passée. */
  expiredLabel?: string
  prefix?: string
  /**
   * Heure du serveur au moment de la réponse (ISO). Corrige l'horloge du poste
   * (souvent décalée) : l'heure qui fait foi est celle du serveur.
   */
  serverTime?: string
}

export function formatRemaining(ms: number): string {
  const minutes = Math.floor(ms / 60_000)
  const days = Math.floor(minutes / 1440)
  const hours = Math.floor((minutes % 1440) / 60)
  const mins = minutes % 60
  if (days > 0) return `${days} j ${hours} h`
  if (hours > 0) return `${hours} h ${String(mins).padStart(2, '0')} min`
  return `${Math.max(mins, 0)} min`
}

/**
 * Compte à rebours d'une date limite (clôture des dépôts, paiement).
 * Indicatif seulement : le backend décide toujours si le dépôt est ouvert.
 */
export function Countdown({
  deadline,
  expiredLabel = 'Clôturé',
  prefix = 'Clôture dans',
  serverTime,
}: CountdownProps) {
  const target = new Date(deadline).getTime()
  // Décalage figé à la réception de la réponse : serveur − poste.
  const [offset] = useState(() => (serverTime ? new Date(serverTime).getTime() - Date.now() : 0))
  const [now, setNow] = useState(() => Date.now() + offset)

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now() + offset), 30_000)
    return () => clearInterval(timer)
  }, [offset])

  const remaining = target - now
  const formatted = new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(target)

  if (remaining <= 0) {
    return <Badge variant="outline">{expiredLabel}</Badge>
  }

  const variant =
    remaining < 24 * 3600_000 ? 'danger' : remaining < 72 * 3600_000 ? 'warning' : 'success'
  return (
    <Badge variant={variant} dot title={formatted}>
      {prefix} <span className="countdown">{formatRemaining(remaining)}</span>
    </Badge>
  )
}
